import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Dialog, DialogContentDrawerRenderProps, Spinner } from '@economic/taco';
import { User } from '@economic/react-app';
import { tBB } from '../../../../../../../shared/components/l10n/l10n';

import ConsentStep from './ConsentStep';
import CompanyDataStep from './CompanyDataStep';
import UserDataStep from './UserDataStep';
import PaymentDataStep from './PaymentDataStep/PaymentDataStep';
import InformationSentStep from './InformationSentStep/InformationSentStep';
import SelectPlanStep from './SelectPlanStep';
import ModalFooter from '../ModalFooter/ModalFooter';

import track from '../../../../../../../shared/util/utilities/mixpanel';
import internalApiClient from '../../../../../../../shared/util/api/internalApiClient';
import { Journal, OnboardingStatus } from '../../../../../../../../types/internal-api';
import {
    SMARTPAY_SIGNUP_STEPS,
    SmartPayIntegrationError,
    SMARTPAY_PLAN_OPTIONS,
    SmartPayOnboardingStatus,
    SmartPayCustomerType,
} from '../../../types';
import { ProgressDots } from '../../../../../../../shared/components/ui/progressDots/ProgressDots';
import { sanitizeCVRNumber } from './utils';

interface Props {
    currentFlowSteps: string[];
    loadingTexts: boolean;
    isDataLoading: boolean;
    onDialogClose: () => void;
    currentStepIndex: number;
    setCurrentStepIndex: Dispatch<SetStateAction<number>>;
    onboardingData: OnboardingStatus;
    userData: User;
    journals: Journal[];
    paymentAccounts: [];
    feeAccounts: [];
    refetchState?: () => void;
    inMarket?: boolean;
    novemberFirstCustomerData: any;
    drawerProps?: DialogContentDrawerRenderProps;
    preSelectedJournalName: string | undefined;
}

const SignupDialogContent = ({
    currentFlowSteps,
    loadingTexts,
    isDataLoading,
    onDialogClose,
    currentStepIndex,
    setCurrentStepIndex,
    onboardingData,
    userData,
    journals,
    paymentAccounts,
    feeAccounts,
    refetchState,
    inMarket,
    novemberFirstCustomerData,
    drawerProps,
    preSelectedJournalName,
}: Props) => {
    const [isSendingOnboardingInfo, setIsSendingOnboardingInfo] = useState(false);
    const [errorCode, setErrorCode] = useState<string | null>(null);

    const customerType: SmartPayCustomerType = novemberFirstCustomerData?.customerType
        ? novemberFirstCustomerData.customerType
        : SmartPayCustomerType.NewCustomer;
    const cvrAlreadyOnboarded = !!novemberFirstCustomerData?.cvrAlreadyOnboarded;
    const anotherOnboardingAlreadyInProgress = !!novemberFirstCustomerData?.onboardingAlreadyInProgress;
    const allCustomersOnboarded = !!novemberFirstCustomerData?.allCustomersOnboarded;
    const novemberFirstCustomerNumberList: string[] = novemberFirstCustomerData?.customerNumbers
        ? novemberFirstCustomerData.customerNumbers
        : [];

    const planState = useState(SMARTPAY_PLAN_OPTIONS[0].id);

    // user data
    const firstNameState = useState(userData.firstName);
    const lastNameState = useState(userData.lastName);
    const emailState = useState(userData.email);
    const phoneState = useState('');
    const newsletterConsentState = useState(false);

    // payment data
    const bankAccountState = useState<number>();
    const [journalId, setJournalId] = useState<number>();
    const feeAccountState = useState<number>();
    const [selectedCustomerNumber, setSelectedCustomerNumber] = useState<string>();

    useEffect(() => {
        if (currentFlowSteps[currentStepIndex] === SMARTPAY_SIGNUP_STEPS.INFORMATION_SENT) {
            postOnboardingInfo();
        }

        if (currentFlowSteps[currentStepIndex] === SMARTPAY_SIGNUP_STEPS.PAYMENT_DATA && newsletterConsentState[0]) {
            subscribeToNewsletter();
        }
    }, [currentStepIndex]);

    useEffect(() => {
        // preselect recently added journal
        if (!preSelectedJournalName) return;

        const preSelectedJournal: Journal | undefined = journals.find((j: Journal) => j.name === preSelectedJournalName);

        if (!preSelectedJournal) return;
        setJournalId(preSelectedJournal.id);
    }, [preSelectedJournalName]);

    const onNextStep = () => {
        setCurrentStepIndex(currentStepIndex + 1);
    };

    const onBackStep = () => {
        setCurrentStepIndex(currentStepIndex - 1);
    };

    const trackError = (error: string) => {
        if (!error) return;

        let eventSlug = 'signup.modal.error-generic';

        switch (error) {
            case SmartPayIntegrationError.SMARTPAY_ONBOARDING_MODAL_ERROR_STATE_ACCOUNT_NUMBER_LENGTH_INVALID:
                eventSlug = 'signup.modal.error-bankaccount-length';
                break;
            case SmartPayIntegrationError.SMARTPAY_ONBOARDING_MODAL_ERROR_STATE_BANK_NOT_SUPPORTED:
                eventSlug = 'signup.modal.error-bank-notsupported';
                break;
            case SmartPayIntegrationError.NEWSLETTER_SUBSCRIBE:
                eventSlug = 'signup.modal.error-newsletter-subscribe';
                break;
        }

        track('smartpay', eventSlug, { errorMessage: tBB(error) });
    };

    const postOnboardingInfo = async () => {
        setIsSendingOnboardingInfo(true);

        try {
            const onboardingPayload = {
                CustomerType: customerType,
                Daybook: journalId,
                PaymentAccount: bankAccountState[0],
                FeeAccount: feeAccountState[0],
                Cvr: sanitizeCVRNumber(onboardingData.companyData.cvr),
                Telephone: phoneState[0],
                Email: emailState[0],
                CustomerNumber: selectedCustomerNumber,
                FirstName: firstNameState[0],
                LastName: lastNameState[0],
                SubscriptionType: planState[0],
            };

            const res = await internalApiClient.post('/smartpay/initiateOnboarding', onboardingPayload);

            if (res.status === 201) {
                setIsSendingOnboardingInfo(false);
            }
        } catch (e) {
            setIsSendingOnboardingInfo(false);

            let errorMessage = 'GENERIC_ERROR_MESSAGE';

            if (e.response.data.message) {
                errorMessage = e.response.data.message;
            }

            if (e.response.data.daybook.length > 0) {
                const [daybookError] = e.response.data.daybook;
                errorMessage = daybookError;
            }
            trackError(errorMessage);
            setErrorCode(errorMessage);
        }
    };

    const subscribeToNewsletter = async () => {
        const marketingConsentText = tBB('SMARTPAY_ONBOARDING_MODAL_NEWSLETTER_CONSENT')
            .join('')
            .replace('[__LINK]', 'here (https://www.e-conomic.dk/lpd/nyhedsmails-app-partners)');

        try {
            await internalApiClient.post('/Users/subscribeSmartPay', { email: emailState[0], marketingConsentText });
        } catch (error) {
            trackError(SmartPayIntegrationError.NEWSLETTER_SUBSCRIBE);
        }
    };

    return (
        <>
            {loadingTexts ? (
                <div className="flex flex-col items-center justify-center h-128">
                    <Spinner />
                </div>
            ) : (
                <>
                    <Dialog.Title>
                        <ProgressDots
                            stepsCount={currentFlowSteps.length}
                            currentStep={currentStepIndex + 1}
                            withCompleteTicks={false}
                        />
                    </Dialog.Title>

                    {currentFlowSteps[currentStepIndex] === SMARTPAY_SIGNUP_STEPS.CONSENT ? (
                        <ConsentStep
                            isDataLoading={isDataLoading}
                            onDialogClose={onDialogClose}
                            next={onNextStep}
                            anotherOnboardingAlreadyInProgress={anotherOnboardingAlreadyInProgress}
                            cvrAlreadyOnboarded={cvrAlreadyOnboarded}
                            allCustomersOnboarded={allCustomersOnboarded}
                        />
                    ) : currentFlowSteps[currentStepIndex] === SMARTPAY_SIGNUP_STEPS.SELECT_PLAN ? (
                        <SelectPlanStep
                            planState={planState}
                            // user will always be unregistered if this modal is shown
                            status={SmartPayOnboardingStatus.Unregistered}
                            next={onNextStep}
                            back={onBackStep}
                        />
                    ) : currentFlowSteps[currentStepIndex] === SMARTPAY_SIGNUP_STEPS.COMPANY_DATA ? (
                        <CompanyDataStep next={onNextStep} back={onBackStep} companyData={onboardingData.companyData} />
                    ) : currentFlowSteps[currentStepIndex] === SMARTPAY_SIGNUP_STEPS.USER_DATA ? (
                        <UserDataStep
                            next={onNextStep}
                            back={onBackStep}
                            userDataStates={{
                                firstNameState,
                                lastNameState,
                                emailState,
                                phoneState,
                                newsletterConsentState,
                            }}
                        />
                    ) : currentFlowSteps[currentStepIndex] === SMARTPAY_SIGNUP_STEPS.PAYMENT_DATA ? (
                        <PaymentDataStep
                            next={onNextStep}
                            back={onBackStep}
                            journals={journals}
                            journalsState={[journalId, setJournalId]}
                            bankAccounts={paymentAccounts}
                            bankAccountState={bankAccountState}
                            feeAccounts={feeAccounts}
                            feeAccountState={feeAccountState}
                            customerNumberState={[selectedCustomerNumber, setSelectedCustomerNumber]}
                            customerNumberOptions={novemberFirstCustomerNumberList}
                            customerType={customerType}
                            allCustomersOnboarded={allCustomersOnboarded}
                            onDialogClose={onDialogClose}
                            drawerProps={drawerProps}
                        />
                    ) : currentFlowSteps[currentStepIndex] === SMARTPAY_SIGNUP_STEPS.INFORMATION_SENT ? (
                        isSendingOnboardingInfo ? (
                            <div className="flex justify-center items-center w-full " style={{ height: '500px' }}>
                                <Spinner delay={0} />
                            </div>
                        ) : (
                            <InformationSentStep
                                errorCode={errorCode}
                                setErrorCode={setErrorCode}
                                back={onBackStep}
                                onDialogClose={onDialogClose}
                                refetchState={refetchState}
                                inMarket={inMarket}
                                email={emailState[0]}
                                customerType={customerType}
                            />
                        )
                    ) : (
                        onDialogClose()
                    )}
                </>
            )}

            <ModalFooter />
        </>
    );
};

export default SignupDialogContent;
